/**
 * RationaleFields — the four-part rationale at a decision moment.
 *
 * One form for the five moments the canon names (DECISION_INFRASTRUCTURE_DESIGN
 * §1, P26; Brief K slice 4): a pool published, criteria locked, a cut line
 * placed, a realignment recorded, a rank overridden. Each act carries what is
 * written here onto the Decision it already writes (migration 985).
 *
 * Every field is optional and the form never blocks the act (canon 02B L2: the
 * person decides). Nothing written means nothing is sent, and the decision page
 * then says no rationale was recorded — only when none was.
 *
 * The labels are the ones the decision page reads them back under
 * (DecisionRecordView): Context at the time · Reasoning · Alternatives
 * considered · Revisit when · Review by.
 *
 * Replaces RationaleCapture (V1.1, migration 464), which only the Realignment
 * space used and which could mark fields required.
 */
import { Disclosure, Field, TextArea, TextInput } from '@/components/ds';
import { StructuralAlternatives } from '../StructuralAlternatives';
import type { ComparisonSet } from '@/lib/rejectedAlternatives';
import { EMPTY_RATIONALE, toRationaleParts, type RationaleValue } from '@/lib/rationaleParts';

export interface RationaleFieldsProps {
  value?: RationaleValue;
  onChange: (value: RationaleValue) => void;
  /** The question the moment asks, as the section's title ("Why these criteria"). */
  title?: string;
  /**
   * Leave out the Reasoning field when the act already asks for its reason
   * in its own words (an override's required reason, a published pool's
   * cut-line reason). That reason is the reasoning on the record.
   */
  omitReasoning?: boolean;
  /** Open on first render. Closed by default: the act is one click without it. */
  defaultOpen?: boolean;
  disabled?: boolean;
  /**
   * The locked comparison set the decision is made against, when there is one
   * (PRD P2): shows the below-the-line items it beat above the free-text
   * alternatives, which then carry only what was never in the set.
   */
  comparisonSet?: ComparisonSet;
  className?: string;
}

const HINTS = {
  context: 'What was true when this was decided?',
  reasoning: 'Why does this make sense, given that context?',
  alternatives: 'What else was considered, and why was it passed over?',
  alternativesOutsideSet: 'Anything considered that was never in the set: a vendor, doing nothing, another approach?',
  revisit: 'What would make you reconsider this?',
  reviewBy: 'When should this be looked at again?',
} as const;

function writtenCount(value: RationaleValue, omitReasoning: boolean): number {
  const parts = toRationaleParts(value);
  if (!parts) return 0;
  return (
    (parts.rationale_context ? 1 : 0) +
    (!omitReasoning && parts.rationale_reasoning ? 1 : 0) +
    (parts.rationale_alternatives ? 1 : 0) +
    (parts.reconsideration_conditions ? 1 : 0)
  );
}

export function RationaleFields({
  value = EMPTY_RATIONALE,
  onChange,
  title = 'Why',
  omitReasoning = false,
  defaultOpen = false,
  disabled = false,
  comparisonSet,
  className = '',
}: RationaleFieldsProps) {
  const update = (field: keyof RationaleValue, next: string) => onChange({ ...value, [field]: next });
  const total = omitReasoning ? 3 : 4;
  const written = writtenCount(value, omitReasoning);

  return (
    <div className={`border border-[var(--rule)] ${className}`}>
      <Disclosure
        label="Optional"
        title={title}
        meta={written > 0 ? `${written} of ${total} written` : 'Nothing written'}
        defaultOpen={defaultOpen}
        disabled={disabled}
      >
        <div className="space-y-4 pt-1">
          <Field label="Context at the time" hint={HINTS.context}>
            <TextArea
              rows={2}
              value={value.rationaleContext}
              onChange={(e) => update('rationaleContext', e.target.value)}
              disabled={disabled}
            />
          </Field>
          {!omitReasoning && (
            <Field label="Reasoning" hint={HINTS.reasoning}>
              <TextArea
                rows={2}
                value={value.rationaleReasoning}
                onChange={(e) => update('rationaleReasoning', e.target.value)}
                disabled={disabled}
              />
            </Field>
          )}
          <div className="space-y-2">
            {comparisonSet && <StructuralAlternatives comparisonSet={comparisonSet} />}
            <Field
              label="Alternatives considered"
              hint={comparisonSet ? HINTS.alternativesOutsideSet : HINTS.alternatives}
            >
              <TextArea
                rows={2}
                value={value.rationaleAlternatives}
                onChange={(e) => update('rationaleAlternatives', e.target.value)}
                disabled={disabled}
              />
            </Field>
          </div>
          <Field label="Revisit when" hint={HINTS.revisit}>
            <TextArea
              rows={2}
              value={value.reconsiderationConditions}
              onChange={(e) => update('reconsiderationConditions', e.target.value)}
              disabled={disabled}
            />
          </Field>
          <Field label="Review by" hint={HINTS.reviewBy}>
            <TextInput
              type="date"
              className="max-w-[12rem]"
              value={value.reviewByDate}
              onChange={(e) => update('reviewByDate', e.target.value)}
              disabled={disabled}
            />
          </Field>
        </div>
      </Disclosure>
    </div>
  );
}

export default RationaleFields;
